import { useSyncExternalStore } from "react";

/** Typeface, text size and focus mode for the writing column, kept per device like the theme. */
export type FontSetting = "serif" | "sans" | "mono";
export type TextSizeSetting = "small" | "medium" | "large" | "larger";

const FONTS: readonly FontSetting[] = ["serif", "sans", "mono"];
const SIZES: readonly TextSizeSetting[] = ["small", "medium", "large", "larger"];

/**
 * One setting stored in localStorage and mirrored to a data attribute on <html>
 * (styles.css maps the attribute to CSS variables). The default value is neither stored
 * nor set as an attribute.
 */
function deviceSetting<T extends string>(key: string, attr: "font" | "textSize" | "focus", values: readonly T[], fallback: T) {
  const listeners = new Set<() => void>();
  let value = fallback;
  try {
    const v = localStorage.getItem(key) as T | null;
    if (v && values.includes(v)) value = v;
  } catch {
    // ignore
  }

  const apply = () => {
    const root = document.documentElement;
    if (value === fallback) delete root.dataset[attr];
    else root.dataset[attr] = value;
  };

  return {
    apply,
    set(next: T) {
      value = next;
      try {
        if (next === fallback) localStorage.removeItem(key);
        else localStorage.setItem(key, next);
      } catch {
        // ignore
      }
      apply();
      listeners.forEach((l) => l());
    },
    subscribe(fn: () => void) {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    get: () => value,
  };
}

const font = deviceSetting<FontSetting>("quill.font", "font", FONTS, "serif");
const textSize = deviceSetting<TextSizeSetting>("quill.textSize", "textSize", SIZES, "medium");
/** Focus mode: the caret's line held at one height on screen, other paragraphs dimmed (focus.ts). */
const focus = deviceSetting<"off" | "on">("quill.focus", "focus", ["off", "on"], "off");

export function applyTypography() {
  font.apply();
  textSize.apply();
  focus.apply();
  applyPageWidth();
}

export const setFont = font.set;
export const setTextSize = textSize.set;

export function useFont(): FontSetting {
  return useSyncExternalStore(font.subscribe, font.get);
}

export function useTextSize(): TextSizeSetting {
  return useSyncExternalStore(textSize.subscribe, textSize.get);
}

export const toggleFocusMode = () => focus.set(focus.get() === "on" ? "off" : "on");

export function useFocusMode(): boolean {
  return useSyncExternalStore(focus.subscribe, focus.get) === "on";
}

/** Writing column width in characters (ch of the writing font), set by dragging its edges. */
export const PAGE_WIDTH = { default: 65, min: 40, max: 200 };

const WIDTH_KEY = "quill.pageWidth";
const widthListeners = new Set<() => void>();

function readWidth(): number {
  try {
    const v = Number(localStorage.getItem(WIDTH_KEY));
    if (Number.isInteger(v) && v >= PAGE_WIDTH.min && v <= PAGE_WIDTH.max) return v;
  } catch {
    // ignore
  }
  return PAGE_WIDTH.default;
}

let pageWidth = readWidth();

export function applyPageWidth() {
  const style = document.documentElement.style;
  if (pageWidth === PAGE_WIDTH.default) style.removeProperty("--measure");
  else style.setProperty("--measure", `${pageWidth}ch`);
}

export function setPageWidth(next: number) {
  next = Math.round(Math.min(PAGE_WIDTH.max, Math.max(PAGE_WIDTH.min, next)));
  if (next === pageWidth) return;
  pageWidth = next;
  try {
    if (next === PAGE_WIDTH.default) localStorage.removeItem(WIDTH_KEY);
    else localStorage.setItem(WIDTH_KEY, String(next));
  } catch {
    // ignore
  }
  applyPageWidth();
  widthListeners.forEach((l) => l());
}

export function usePageWidth(): number {
  return useSyncExternalStore(
    (fn) => {
      widthListeners.add(fn);
      return () => widthListeners.delete(fn);
    },
    () => pageWidth,
  );
}
